import classNames from "classnames";
import type { UserVariables } from "components/shared/AgreementModal";
import { FileButton } from "components/shared/MultiStep/ImportFile";
import ScrollShadowWrapper from "components/shared/ScrollShadowWrapper";
import StyledButton from "components/shared/StyledButton";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import { Body, H3, PanelTitle, SmallBody } from "helpers/TypographyComponents";
import { getStatementFilename } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { AgreementSigner } from "store/agreementsStore";
import { fetchAgreementPreview } from "store/agreementsStore";
import type { IArticle } from "store/articleStore";
import type { ITerm } from "store/managementStore";
import { useAppDispatch } from "store/store";
import type { IUser } from "types";

import styles from "../style.module.scss";

interface IVerifySignatoriesEmail {
    relatedResourceData: IArticle | ITerm;
    userVariables?: UserVariables;
    agreementId: number;
    /**
     * Send email form action (send for validation)
     */
    onSend: (beneficiaryEmails: TBeneficiaryEmail[]) => Promise<void>;
    publisherSigner: IUser | undefined;
    /**
     * Loading btn status for validation btn
     */
    isLoadingButton: "approve" | "decline" | "send-for-validation" | null;
    /**
     * Default state for beneficiaryEmails
     */
    defaultBeneficiaryEmails: TBeneficiaryEmail[];
    /**
     * Related beneficiaries
     */
    beneficiaries: AgreementSigner[];
    /**
     * Check if agreement has validators
     */
    hasValidators: boolean;
    /**
     * Go to previous step
     */
    handlePreviousStep: () => void;
}

export type TBeneficiaryEmail = { id: number; email: string };

export const VerifySignatoriesEmail = ({
    relatedResourceData,
    agreementId,
    onSend,
    publisherSigner,
    isLoadingButton,
    defaultBeneficiaryEmails,
    beneficiaries,
    hasValidators,
    handlePreviousStep,
}: IVerifySignatoriesEmail) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { companyInfos } = useCompanySettings();
    const [beneficiaryEmails, setBeneficiaryEmails] = useState<TBeneficiaryEmail[]>(defaultBeneficiaryEmails);

    const hasEmptyEmail = beneficiaryEmails?.some((beneficiary) => !beneficiary.email);

    const getEmailValue = (beneficiaryId: number) =>
        beneficiaryEmails?.find((beneficiar) => beneficiar.id === beneficiaryId)?.email;

    const handleChangeEmail = (e: any, beneficiaryId: number) => {
        const emailValue = e.target.value;
        setBeneficiaryEmails((prev) => {
            const state = structuredClone(prev) as TBeneficiaryEmail[];
            const changedField = state?.find((beneficiary) => beneficiary.id === beneficiaryId) as TBeneficiaryEmail;
            const index = state?.indexOf(changedField);
            state[index].email = emailValue;
            return state;
        });
    };

    const handleSend = () => {
        onSend(beneficiaryEmails);
    };

    const handleFileDownload = () => {
        const filename = getStatementFilename(
            relatedResourceData.author ?? "",
            relatedResourceData.title ?? "",
            t("Summary.Contract"),
            new Date(),
        );

        dispatch(
            fetchAgreementPreview({
                filename,
                agreementId,
            }),
        );
    };

    return (
        <div className={styles["agreement-send-for-validation-step"]}>
            <div className={styles["step-wrapper"]}>
                <div className={styles["step-content-top"]}>
                    <div className={styles["step-head"]}>
                        <PanelTitle>{t("ContractValidation.VerifyEmail")}</PanelTitle>
                        <Body color="grey">
                            {relatedResourceData.author}{" "}
                            {hasValidators
                                ? t("ContractSettings.SignatureStep2SubtitleValidation")
                                : t("ContractSettings.SignatureStep2Subtitle")}
                        </Body>
                    </div>
                    <div className={styles["step-body-verify-data"]} style={{ gap: "1.5rem" }}>
                        <ScrollShadowWrapper style={{ maxHeight: "14rem" }}>
                            <div className={styles["email-fields-wrapper"]}>
                                {beneficiaries?.map((beneficiary) => {
                                    const beneficiaryId = beneficiary.relatedResourceId;
                                    return (
                                        <div
                                            className={styles["field-beneficiary"]}
                                            key={`signer-field-${beneficiaryId}`}
                                        >
                                            <div className={styles["label"]}>
                                                <H3 color="grey">
                                                    {t("Articles.EmailOf")} {beneficiary.name ?? ""}
                                                </H3>
                                                <SmallBody color="grey">
                                                    ({t("ContractValidationEdit.Author")})
                                                </SmallBody>
                                            </div>
                                            <StyledTextField
                                                value={getEmailValue(beneficiaryId)}
                                                onChange={(e) => handleChangeEmail(e, beneficiaryId)}
                                                fullWidth
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </ScrollShadowWrapper>
                        <div className={styles["publisher-field"]}>
                            <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
                                <H3 color="grey">
                                    {t("Articles.EmailOf")} {publisherSigner?.firstname ?? ""}{" "}
                                    {publisherSigner?.lastname ?? ""}
                                </H3>
                                <SmallBody color="grey">({companyInfos?.name ?? ""})</SmallBody>
                            </div>
                            <StyledTextField value={publisherSigner?.email ?? ""} disabled fullWidth />
                        </div>
                        <FileButton
                            label={t("ContractSettings.DownloadPreviewFile")}
                            onClick={handleFileDownload}
                            style={{ width: "100%" }}
                        />
                    </div>
                </div>
                <div
                    className={classNames({
                        [styles["step-actions"]]: true,
                        [styles["double-action"]]: hasValidators,
                    })}
                >
                    {hasValidators && (
                        <StyledButton buttonType="secondary" onClick={handlePreviousStep}>
                            {t("Agreements.ReturnToApprovers")}
                        </StyledButton>
                    )}
                    <StyledButton
                        buttonType="primary"
                        disabled={hasEmptyEmail}
                        onClick={handleSend}
                        loading={isLoadingButton === "send-for-validation"}
                    >
                        {t("ContractValidationEdit.Send")}
                    </StyledButton>
                </div>
            </div>
        </div>
    );
};

export default VerifySignatoriesEmail;
